import { QueryClient } from "@tanstack/react-query";
import { invalidateCentralResources, type CentralResource } from "@/shared/lib/cacheInvalidations";

/**
 * Meta configuration for mutations consumed by the global mutation handler
 * below. Mutations can declare what to invalidate via meta and the handler
 * dispatches the invalidations once the mutation succeeds.
 */
type MutationMeta = {
  invalidateQueries?: readonly (readonly unknown[])[]
  invalidateResources?: readonly CentralResource[]
};

/**
 * Wire the global mutation handler that reads `mutation.meta` to run
 * declarative invalidations (invalidateQueries + invalidateResources).
 *
 * Extracted so that test query clients can opt into the same behavior
 * as the production singleton.
 */
export const setupMutationMetaHandlers = (client: QueryClient): void => {
  const mutationCache = client.getMutationCache();

  mutationCache.config.onSuccess = (_data, _variables, _context, mutation) => {
    const meta = mutation.meta as MutationMeta | undefined;
    if (!meta) return;

    if (meta.invalidateQueries) {
      meta.invalidateQueries.forEach((queryKey) => {
        client.invalidateQueries({ queryKey });
      });
    }

    if (meta.invalidateResources?.length) {
      invalidateCentralResources(client, meta.invalidateResources);
    }
  };
};

/**
 * Query client for React Query
 * @returns Query client
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 1 },
  },
});

setupMutationMetaHandlers(queryClient);
